import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "./apiConfig";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import {
  FaClipboardList, FaCheckCircle, FaTimesCircle, FaClock,
  FaSearch, FaEye, FaGraduationCap, FaIdCard, FaTag,
} from "react-icons/fa";
import { DetailPanel as StudentDetailPanel } from "./RegistrationApproval";
import { DetailPanel as CustomerDetailPanel } from "./LaneCustomerApproval";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { useDateTimeFormat } from "./utils/formatters";
import { useSort } from "./hooks/useSort";

const BASE   = API_BASE;
/* ── Unified status tabs ──────────────────────────────────────────────────── */
// unified tabs: pending | approved | rejected
// Student statuses: pending / approved / rejected
// Customer statuses: pending / active  / inactive  (mapped → approved/rejected)

const TABS = ["pending","approved","rejected"];

// Single source of truth for the table's columns — driving both the header
// row and the data rows from the same list keeps their widths and text
// alignment locked together, so a header can never drift out of line with
// the cells under it (the cause of the table's previous cramped/uneven
// look: mismatched widths, no gap between columns, and a right-aligned
// Status cell under a left-aligned Status header).
const COLUMNS = [
  { key:"_type",         label:"Type",               width:"104px" },
  { key:"_refId",         label:"ID",                 width:"104px" },
  { key:"_name",          label:"Name",               width:"180px" },
  { key:"_contact",       label:"Contact",            width:"minmax(190px,1.3fr)" },
  { key:"_detail",        label:"Program / Purpose",  width:"160px" },
  { key:"_dateCreated",   label:"Created",            width:"104px" },
  { key:"_dateApproved",  label:"Approved",           width:"104px" },
  { key:"_approvedBy",    label:"Approved By",        width:"130px" },
  { key:"_unified",       label:"Status",             width:"130px", align:"right" },
];
const GRID_COLUMNS = COLUMNS.map(c => c.width).join(" ");
const GRID_MIN_WIDTH = 1120;

const STATUS_META = {
  pending:  { label:"Pending",  color:getBrandColors().orange, bg:"rgba(217,119,6,0.08)",  icon:<FaClock size={11}/> },
  approved: { label:"Approved", color:getBrandColors().green, bg:"rgba(5,150,105,0.08)",  icon:<FaCheckCircle size={11}/> },
  rejected: { label:"Rejected", color:getBrandColors().red, bg:"rgba(220,38,38,0.08)",  icon:<FaTimesCircle size={11}/> },
};

// Map customer raw status → unified tab key
function normalizeCustomerStatus(s) {
  if (s === "active")   return "approved";
  if (s === "inactive") return "rejected";
  return "pending"; // covers "pending" and anything else
}

// Map unified tab → raw customer status for API call
function customerApiStatus(tab) {
  if (tab === "approved") return "active";
  if (tab === "rejected") return "inactive";
  return "pending";
}

function TypeBadge({ type }) {
  const isStudent = type === "student";
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:4,
      padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700,
      background: isStudent ? `rgba(247,206,11,0.15)` : "rgba(99,102,241,0.12)",
      color:       isStudent ? getBrandColors().blue                    : "#6366f1",
      border:     `1px solid ${isStudent ? getBrandColors().yellow+"66" : "rgba(99,102,241,0.3)"}`,
    }}>
      {isStudent ? <FaGraduationCap size={9}/> : <FaIdCard size={9}/>}
      {isStudent ? "Student" : "Customer"}
    </span>
  );
}

function StatusChip({ unifiedStatus }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const m = STATUS_META[unifiedStatus] || STATUS_META.pending;
  const tone = unifiedStatus==="pending" ? "warning" : (unifiedStatus==="approved"||unifiedStatus==="active") ? "success" : "danger";
  const pill = T.statusPill(tone);
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:4,
      padding:"3px 9px", borderRadius:20, fontSize:10, fontWeight:700,
      background:pill.background, color:pill.color, border:pill.border,
    }}>
      {m.icon} {m.label}
    </span>
  );
}

/* ── Type filter pill ─────────────────────────────────────────────────────── */
const TYPE_FILTERS = ["all","student","customer"];

/* ── Main component ───────────────────────────────────────────────────────── */
export default function ApprovalQueue({ currentUser }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { fmtDate } = useDateTimeFormat();

  const [activeTab,   setActiveTab]   = useState("pending");
  const [typeFilter,  setTypeFilter]  = useState("all");
  const [search,      setSearch]      = useState("");
  const [loading,     setLoading]     = useState(true);
  const [rows,        setRows]        = useState([]);       // unified row list
  const [selected,    setSelected]    = useState(null);     // { type, data }
  const [counts,      setCounts]      = useState({ pending:0, approved:0, rejected:0 });

  // Static data for panels
  const [programsList,    setProgramsList]    = useState([]);
  const [membershipPlans, setMembershipPlans] = useState([]);

  // Load panel dependencies once
  useEffect(() => {
    fetch(`${BASE}/coaching-programs/`)
      .then(r=>r.json())
      .then(d=>setProgramsList(d.programs||[]))
      .catch(()=>{});
    fetch(`${BASE}/lb/membership-plans`)
      .then(r=>r.json())
      .then(d=>setMembershipPlans(Array.isArray(d)?d:(d.plans||[])))
      .catch(()=>{});
  }, []);

  /* ── Fetch both queues in parallel ───────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const studentStatus   = activeTab; // pending / approved / rejected
      const customerStatus  = customerApiStatus(activeTab);

      const [sRes, cRes] = await Promise.all([
        fetch(`${BASE}/registration/?status=${studentStatus}`),
        fetch(`${BASE}/lb/clients?status=${customerStatus}&is_student=0`),
      ]);
      const sData = await sRes.json();
      const cData = await cRes.json();

      const studentRows = (sData.submissions || []).map(s => ({
        _type:   "student",
        _id:     `s-${s.submission_id}`,
        _refId:  s.submission_id,
        _refLabel: `REG-${String(s.submission_id).padStart(5,"0")}`,
        _name:   `${s.first_name} ${s.last_name}`,
        _contact: s.parent_email || s.parent_name || "—",
        _detail: s.program_name || "—",
        _dateCreated:  s.submitted_at,
        _dateApproved: s.reviewed_at || null,
        _approvedBy:   s.reviewed_by || null,
        // Sorts pending items by when they were submitted (oldest/newest
        // still waiting), and approved/rejected items by when they were
        // actually acted on — a single default that answers both "who's
        // pending" and "who did I just approve" without extra clicks.
        _activityDate: s.reviewed_at || s.submitted_at,
        _status: s.status,         // raw student status
        _unified: s.status,        // same for students
        data:    s,
      }));
      const customerRows = (Array.isArray(cData)?cData:(cData.clients||[])).map(c => ({
        _type:   "customer",
        _id:     `c-${c.client_id}`,
        _refId:  c.client_id,
        _refLabel: `CLI-${String(c.client_id).padStart(5,"0")}`,
        _name:   `${c.first_name} ${c.last_name}`,
        _contact: c.email || "—",
        _detail: c.registration_purpose?.replace(/_/g," ") || "—",
        _dateCreated:  c.created_at,
        _dateApproved: c.reviewed_at || null,
        _approvedBy:   c.reviewed_by || null,
        _activityDate: c.reviewed_at || c.created_at,
        _status: c.status,                         // raw customer status
        _unified: normalizeCustomerStatus(c.status),
        data:    c,
      }));

      setRows([...studentRows, ...customerRows]);
    } catch (e) {
      console.error("ApprovalQueue load error:", e);
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { load(); setSelected(null); }, [load]);

  /* ── Counts — parallel aggregate fetch ───────────────────────────────────── */
  const loadCounts = useCallback(() => {
    const tabs = ["pending","approved","rejected"];
    Promise.all(tabs.map(async tab => {
      const sStat = tab;
      const cStat = customerApiStatus(tab);
      const [sR, cR] = await Promise.all([
        fetch(`${BASE}/registration/?status=${sStat}`).then(r=>r.json()).catch(()=>({ submissions:[] })),
        fetch(`${BASE}/lb/clients?status=${cStat}&is_student=0`).then(r=>r.json()).catch(()=>[]),
      ]);
      const sCount = (sR.submissions||[]).length;
      const cArr   = Array.isArray(cR) ? cR : (cR.clients||[]);
      return { tab, count: sCount + cArr.length };
    })).then(results => {
      const c = {};
      results.forEach(r => { c[r.tab] = r.count; });
      setCounts(c);
    }).catch(()=>{});
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts, rows]);

  /* ── Filtered rows ────────────────────────────────────────────────────────── */
  const filtered = rows.filter(r => {
    if (typeFilter !== "all" && r._type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r._name.toLowerCase().includes(q) || r._contact.toLowerCase().includes(q)
        || (r._refLabel||"").toLowerCase().includes(q) || String(r._refId).includes(q);
    }
    return true;
  });

  // Default: most-recently-acted-on first (approved/rejected date if it has
  // one, else submission date) — so a pending item you just reviewed always
  // rises to the top of its new tab, and the oldest still-pending item is
  // always at the top of the Pending tab. Date Created/Approved/Client ID/
  // Approved By are all independently sortable via their own headers below.
  const { sorted, toggleSort, sortArrow } = useSort(filtered, "_activityDate", "desc");

  /* ── Styles ───────────────────────────────────────────────────────────────── */
  const cardBg = isDark ? "rgba(255,255,255,0.04)" : "#fff";
  const border  = isDark ? "rgba(255,255,255,0.1)"  : "#e2e8f0";

  /* ── Panel close / refresh handlers ──────────────────────────────────────── */
  const handleClose = () => setSelected(null);

  // Student panel calls onRefresh() with no args.
  // Customer panel calls onRefresh("active") | onRefresh("inactive").
  const handleRefresh = useCallback((newStatus) => {
    load();
    loadCounts();
    // Switch tab if customer panel reported new status
    if (newStatus === "active")   { setActiveTab("approved"); setSelected(null); }
    if (newStatus === "inactive") { setActiveTab("rejected"); setSelected(null); }
    // Re-enrich selected student item after approval
    if (!newStatus && selected?.type === "student") {
      const sub = selected.data;
      fetch(`${BASE}/registration/${sub.submission_id}`)
        .then(r=>r.json())
        .then(d=>{ if(d.submission) setSelected(prev=>({...prev, data:d.submission})); })
        .catch(()=>{});
    }
  }, [load, loadCounts, selected]);

  return (
    <div style={{ padding:"20px 24px", fontFamily:"'Segoe UI',system-ui,sans-serif" }}>

      {/* ── Page header ── */}
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:20,
                    padding:"14px 18px", borderRadius:12,
                    background:"rgba(255,255,255,0.15)", backdropFilter:"blur(14px)", WebkitBackdropFilter: "blur(14px)",
                    border:"1px solid rgba(255,255,255,0.25)", width:"fit-content" }}>
        <PageHeaderIcon icon={<FaClipboardList size={20} color={getBrandColors().blueDark} />} />
        <div>
          <h2 style={{ fontSize:20, fontWeight:800, color:isDark?"#f1f5f9":getBrandColors().blue, margin:0 }}>
            Approval Queue
          </h2>
          <div style={{ fontSize:12, color:T.textMuted||"#94a3b8" }}>
            Students &amp; Lane Customers — review and activate in one place
          </div>
        </div>
      </div>

      {/* ── KPI chips ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10,
                    marginBottom:16, maxWidth:480 }}>
        {TABS.map(t => {
          const m = STATUS_META[t];
          return (
            <div key={t} onClick={()=>setActiveTab(t)}
              style={{ background:activeTab===t?m.bg:cardBg,
                       border:`1.5px solid ${activeTab===t?m.color+"60":border}`,
                       borderRadius:10, padding:"10px 14px", cursor:"pointer", transition:"all 0.15s" }}>
              <div style={{ fontSize:11, color:m.color, fontWeight:700, marginBottom:3 }}>{m.label}</div>
              <div style={{ fontSize:22, fontWeight:800, color:isDark?"#f1f5f9":"#1e293b" }}>{counts[t]||0}</div>
            </div>
          );
        })}
      </div>

      {/* ── Tab bar + type filter + search ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                    marginBottom:12, gap:12, flexWrap:"wrap" }}>

        {/* Status tabs */}
        <div style={{ display:"flex", gap:6 }}>
          {TABS.map(t => {
            const m = STATUS_META[t]; const active = activeTab === t;
            return (
              <button key={t} onClick={()=>setActiveTab(t)}
                style={{ padding:"6px 14px", borderRadius:20, border:"none", cursor:"pointer",
                         fontSize:12, fontWeight:700,
                         background:active?`linear-gradient(135deg,${getBrandColors().yellow},#e8b800)`:(isDark?"rgba(255,255,255,0.08)":"#f1f5f9"),
                         color:active?getBrandColors().blue:(isDark?"#94a3b8":"#64748b") }}>
                {m.label}
                {(counts[t]||0)>0 && (
                  <span style={{ marginLeft:4, padding:"1px 6px", borderRadius:10,
                    background:active?getBrandColors().blue+"30":(isDark?"rgba(255,255,255,0.1)":"#e2e8f0"),
                    color:active?getBrandColors().blue:"#94a3b8", fontSize:10 }}>{counts[t]}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Right side: type pills + search */}
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>

          {/* Type filter */}
          <div style={{ display:"flex", gap:4 }}>
            {TYPE_FILTERS.map(tf => (
              <button key={tf} onClick={()=>setTypeFilter(tf)}
                style={{ padding:"5px 12px", borderRadius:20, border:`1px solid ${border}`,
                         cursor:"pointer", fontSize:11, fontWeight:700,
                         background:typeFilter===tf?(tf==="student"?`rgba(247,206,11,0.2)`:(tf==="customer"?"rgba(99,102,241,0.15)":getBrandColors().blue)):cardBg,
                         color:typeFilter===tf?(tf==="student"?getBrandColors().blue:(tf==="customer"?"#6366f1":"#fff")):(isDark?"#94a3b8":"#64748b") }}>
                {tf==="all"?"All":tf==="student"?"Students":"Customers"}
              </button>
            ))}
          </div>

          {/* Search */}
          <div style={{ display:"flex", alignItems:"center", gap:8, background:cardBg,
                        border:`1px solid ${border}`, borderRadius:8, padding:"6px 12px" }}>
            <FaSearch size={11} color="#94a3b8"/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search by name or email…"
              style={{ border:"none", outline:"none", background:"transparent",
                       fontSize:13, color:T.textPrimary||"#1e293b", width:190 }}/>
          </div>

          <button onClick={()=>{ load(); loadCounts(); }}
            style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${border}`,
                     cursor:"pointer", background:cardBg,
                     color:isDark?"#94a3b8":"#64748b", fontSize:12, fontWeight:700 }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* ── Unified table ── */}
      <div style={{ background:cardBg, border:`1px solid ${border}`, borderRadius:12, overflow:"hidden" }}>
       <div style={{ overflowX:"auto" }}>
        <div style={{ minWidth:GRID_MIN_WIDTH }}>

        {/* Header */}
        <div style={{ display:"grid", gridTemplateColumns:GRID_COLUMNS, columnGap:16,
                      padding:"12px 20px", borderBottom:`1px solid ${border}`,
                      background:isDark?"rgba(255,255,255,0.03)":"#f8fafc" }}>
          {COLUMNS.map(({ key, label, align }) => (
            <div key={key} onClick={()=>toggleSort(key)}
              style={{ fontSize:10.5, fontWeight:800, color:"#94a3b8", cursor:"pointer", userSelect:"none",
                       textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap",
                       textAlign: align||"left" }}>{label}{sortArrow(key)}</div>
          ))}
        </div>

        {/* Rows */}
        {loading ? (
          <div style={{ padding:"32px", textAlign:"center", color:"#94a3b8", fontSize:13 }}>Loading…</div>
        ) : sorted.length === 0 ? (
          <div style={{ padding:"32px", textAlign:"center" }}>
            <FaClipboardList size={28} color="#94a3b8" style={{ marginBottom:8, opacity:0.4 }}/>
            <div style={{ color:"#94a3b8", fontSize:13 }}>
              {search ? "No matches found." : `No ${activeTab} registrations.`}
            </div>
          </div>
        ) : sorted.map((r, i) => (
          <div key={r._id}
            onClick={() => {
              setSelected({ type: r._type, data: r.data });
              // Enrich student data with full fetch
              if (r._type === "student") {
                fetch(`${BASE}/registration/${r.data.submission_id}`)
                  .then(x=>x.json())
                  .then(d=>{ if(d.submission) setSelected({ type:"student", data:d.submission }); })
                  .catch(()=>{});
              }
              // Enrich customer data
              if (r._type === "customer") {
                fetch(`${BASE}/lb/clients/${r.data.client_id}`)
                  .then(x=>x.json())
                  .then(d=>{ if(d && d.client_id) setSelected({ type:"customer", data:d }); })
                  .catch(()=>{});
              }
            }}
            style={{ display:"grid", gridTemplateColumns:GRID_COLUMNS, columnGap:16,
                     padding:"13px 20px", cursor:"pointer", alignItems:"center", minHeight:52,
                     borderBottom: i < sorted.length-1 ? `1px solid ${border}` : "none",
                     background: selected?.data?.submission_id === r.data?.submission_id ||
                                 selected?.data?.client_id     === r.data?.client_id
                       ? (isDark?"rgba(3,78,123,0.15)":"#eff6ff")
                       : (i%2===0?"transparent":(isDark?"rgba(255,255,255,0.015)":"#fafafa")),
                     transition:"background 0.12s" }}>

            {/* Type badge */}
            <div style={{ display:"flex", alignItems:"center" }}>
              <TypeBadge type={r._type}/>
            </div>

            {/* ID */}
            <div style={{ fontSize:12, fontWeight:700, color:isDark?"#94a3b8":"#64748b" }}>
              {r._refLabel}
            </div>

            {/* Name */}
            <div style={{ fontSize:13.5, fontWeight:700, color:isDark?"#f1f5f9":"#1e293b",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {r._name}
            </div>

            {/* Contact */}
            <div style={{ fontSize:12.5, color:isDark?"#e2e8f0":"#374151",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {r._contact}
            </div>

            {/* Program / Purpose */}
            <div style={{ fontSize:12.5, color:isDark?"#e2e8f0":"#374151",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                          textTransform:"capitalize" }}>
              {r._detail}
            </div>

            {/* Date created */}
            <div style={{ fontSize:12, color:"#94a3b8", whiteSpace:"nowrap" }}>{fmtDate(r._dateCreated)}</div>

            {/* Date approved (blank while still pending) */}
            <div style={{ fontSize:12, color:"#94a3b8", whiteSpace:"nowrap" }}>
              {r._dateApproved ? fmtDate(r._dateApproved) : "—"}
            </div>

            {/* Approved by (blank while still pending) */}
            <div style={{ fontSize:12, color:isDark?"#e2e8f0":"#374151",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {r._approvedBy || "—"}
            </div>

            {/* Status + eye */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:8 }}>
              <StatusChip unifiedStatus={r._unified}/>
              <FaEye size={12} color="#94a3b8"/>
            </div>
          </div>
        ))}
        </div>
       </div>
      </div>

      {/* ── Detail drawer ── */}
      {selected && (
        <>
          {/* Backdrop */}
          <div onClick={handleClose}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", zIndex:999 }}/>

          {/* Render the right panel based on type */}
          {selected.type === "student" ? (
            <StudentDetailPanel
              sub={selected.data}
              currentUser={currentUser}
              programsList={programsList}
              onClose={handleClose}
              onRefresh={handleRefresh}
            />
          ) : (
            <CustomerDetailPanel
              client={selected.data}
              currentUser={currentUser}
              membershipPlans={membershipPlans}
              onClose={handleClose}
              onRefresh={handleRefresh}
            />
          )}
        </>
      )}
    </div>
  );
}
